import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Button } from "../ui/button";
import { LocationAutocomplete } from "./LocationAutocomplete";

const HUUR_TYPES = [
  { value: "", label: "Type woning" },
  { value: "appartement", label: "Appartement" },
  { value: "huis", label: "Huis" },
  { value: "studio", label: "Studio" },
  { value: "kamer", label: "Kamer" },
];
const KOOP_TYPES = [
  { value: "", label: "Type woning" },
  { value: "huis", label: "Huis" },
  { value: "appartement", label: "Appartement" },
  { value: "villa", label: "Villa" },
];

const HUUR_MAX = [null, 750, 1000, 1250, 1500, 1750, 2000, 2500, 3000, 4000, 5000];
const HUUR_MIN = [null, 500, 750, 1000, 1250, 1500, 2000];
const KOOP_MAX = [null, 200000, 300000, 400000, 500000, 600000, 750000, 1000000, 1500000];
const KOOP_MIN = [null, 100000, 200000, 300000, 400000, 500000, 750000];

const fmt = (n, mode) =>
  n == null ? (mode === "huur" ? "€ (geen limiet)" : "€ (geen limiet)") : `€ ${n.toLocaleString("nl-NL")}`;

/**
 * PropertySearchBar
 * Funda-style unified bar. Reuses existing /woningen and /koopwoningen routes.
 *
 * Props:
 *  - variant: "hero" (large, homepage) | "compact" (sticky, above results)
 *  - defaultMode: "huur" | "koop"
 *  - initial: { city, min_price, max_price, property_type } — from URL when on results page
 */
export const PropertySearchBar = ({
  variant = "hero",
  defaultMode = "huur",
  initial = {},
  className = "",
}) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(defaultMode);
  const [city, setCity] = useState(initial.city || "");
  const [minPrice, setMinPrice] = useState(initial.min_price ? Number(initial.min_price) : null);
  const [maxPrice, setMaxPrice] = useState(initial.max_price ? Number(initial.max_price) : null);
  const [type, setType] = useState(initial.property_type || "");
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => { setMode(defaultMode); }, [defaultMode]);

  // Reset price + type when switching mode so values match currency/type space
  useEffect(() => {
    setMinPrice(null); setMaxPrice(null); setType("");
  }, [mode]);

  const priceMax = mode === "huur" ? HUUR_MAX : KOOP_MAX;
  const priceMin = mode === "huur" ? HUUR_MIN : KOOP_MIN;
  const typeList = mode === "huur" ? HUUR_TYPES : KOOP_TYPES;

  const submit = (e) => {
    e?.preventDefault?.();
    const params = new URLSearchParams();
    if (city) params.set("city", city);
    if (minPrice) params.set("min_price", String(minPrice));
    if (maxPrice) params.set("max_price", String(maxPrice));
    if (type) params.set("property_type", type);
    const base = mode === "koop" ? "/koopwoningen" : "/woningen";
    const qs = params.toString();
    navigate(qs ? `${base}?${qs}` : base);
  };

  const isHero = variant === "hero";

  const wrapperBase = isHero
    ? "rounded-3xl bg-white p-4 shadow-2xl ring-1 ring-black/5 sm:p-5"
    : "rounded-2xl bg-white p-3 shadow-md ring-1 ring-border";

  return (
    <div className={`${wrapperBase} ${className}`} data-testid={`property-search-${variant}`}>
      {/* Huur / Koop tabs */}
      <div className="mb-3 inline-flex items-center gap-1 rounded-full bg-slate-100 p-1">
        {["huur", "koop"].map((m) => (
          <button
            key={m}
            type="button"
            data-testid={`mode-${m}`}
            onClick={() => setMode(m)}
            className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${
              mode === m ? "bg-navy text-white shadow-sm" : "text-slate-500 hover:text-navy"
            }`}
          >
            {m === "huur" ? "Te huur" : "Te koop"}
          </button>
        ))}
      </div>

      <form onSubmit={submit}>
        {/* Row: location + filters + submit */}
        <div className="flex flex-col gap-2 xl:flex-row xl:items-stretch">
          {/* Location grows */}
          <div className="min-w-0 xl:flex-[2] xl:min-w-[240px]">
            <LocationAutocomplete
              value={city}
              onChange={setCity}
              onSelect={(it) => setCity(it.value)}
              onSubmit={submit}
              mode={mode}
              size={isHero ? "md" : "sm"}
              placeholder="Plaats, postcode, wijk of provincie"
              testid="hero-location"
            />
          </div>

          {/* Desktop inline extras (xl+) */}
          <div className="hidden xl:flex xl:items-stretch xl:gap-2">
            <PriceSelect
              testid="hero-min-price"
              label="Min. prijs"
              value={minPrice}
              onChange={setMinPrice}
              options={priceMin}
              placeholder={mode === "huur" ? "Min. prijs" : "Min. prijs"}
              mode={mode}
              size={isHero ? "md" : "sm"}
            />
            <PriceSelect
              testid="hero-max-price"
              label="Max. prijs"
              value={maxPrice}
              onChange={setMaxPrice}
              options={priceMax}
              placeholder={mode === "huur" ? "Max. prijs" : "Max. prijs"}
              mode={mode}
              size={isHero ? "md" : "sm"}
            />
            <TypeSelect
              testid="hero-type"
              value={type}
              onChange={setType}
              options={typeList}
              size={isHero ? "md" : "sm"}
            />
          </div>

          {/* Submit */}
          <Button
            type="submit"
            data-testid="hero-search-btn"
            className={`shrink-0 rounded-full bg-brandblue px-6 font-bold text-white hover:bg-brandblue/90 ${
              isHero ? "h-12" : "h-10"
            }`}
          >
            <Search size={16} className="mr-2" /> Zoeken
          </Button>
        </div>

        {/* Tablet / small desktop: filters on second row (visible md–xl) */}
        <div className="mt-2 hidden md:grid md:grid-cols-3 md:gap-2 xl:hidden">
          <PriceSelect
            testid="hero-min-price-t"
            label="Min. prijs"
            value={minPrice}
            onChange={setMinPrice}
            options={priceMin}
            placeholder={mode === "huur" ? "Min. prijs" : "Min. prijs"}
            mode={mode}
            size={isHero ? "md" : "sm"}
          />
          <PriceSelect
            testid="hero-max-price-t"
            label="Max. prijs"
            value={maxPrice}
            onChange={setMaxPrice}
            options={priceMax}
            placeholder={mode === "huur" ? "Max. prijs" : "Max. prijs"}
            mode={mode}
            size={isHero ? "md" : "sm"}
          />
          <TypeSelect
            testid="hero-type-t"
            value={type}
            onChange={setType}
            options={typeList}
            size={isHero ? "md" : "sm"}
          />
        </div>

        {/* Mobile "Meer filters" toggle (below md) */}
        <div className="mt-3 flex items-center justify-between md:hidden">
          <button
            type="button"
            data-testid="more-filters-toggle"
            onClick={() => setMoreOpen((v) => !v)}
            className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-navy"
          >
            <SlidersHorizontal size={13} />
            {moreOpen ? "Verberg extra filters" : "Meer filters"}
          </button>
          {(minPrice || maxPrice || type) && (
            <button
              type="button"
              onClick={() => { setMinPrice(null); setMaxPrice(null); setType(""); }}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500"
            >
              <X size={12} /> wissen
            </button>
          )}
        </div>
        {moreOpen && (
          <div className="mt-3 grid grid-cols-1 gap-2 md:hidden">
            <PriceSelect
              testid="hero-min-price-m"
              label="Min. prijs"
              value={minPrice}
              onChange={setMinPrice}
              options={priceMin}
              placeholder={mode === "huur" ? "Min. prijs" : "Min. prijs"}
              mode={mode}
              size="sm"
            />
            <PriceSelect
              testid="hero-max-price-m"
              label="Max. prijs"
              value={maxPrice}
              onChange={setMaxPrice}
              options={priceMax}
              placeholder={mode === "huur" ? "Max. prijs" : "Max. prijs"}
              mode={mode}
              size="sm"
            />
            <TypeSelect
              testid="hero-type-m"
              value={type}
              onChange={setType}
              options={typeList}
              size="sm"
            />
          </div>
        )}
      </form>
    </div>
  );
};

const inputCls = (size) =>
  `${size === "sm" ? "h-10 text-sm" : "h-12 text-[15px]"} w-full appearance-none rounded-full border border-border bg-white px-4 pr-8 font-medium text-navy outline-none transition focus:border-brandblue focus:ring-2 focus:ring-brandblue/20`;

const PriceSelect = ({ testid, value, onChange, options, placeholder, mode, size }) => (
  <div className="relative min-w-[130px] xl:min-w-[140px]">
    <select
      data-testid={testid}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      className={inputCls(size)}
    >
      <option value="">{placeholder}</option>
      {options
        .filter((o) => o != null)
        .map((o) => (
          <option key={o} value={o}>{fmt(o, mode)}</option>
        ))}
    </select>
    <ChevronDownIcon />
  </div>
);

const TypeSelect = ({ testid, value, onChange, options, size }) => (
  <div className="relative min-w-[130px] xl:min-w-[140px]">
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={inputCls(size)}
    >
      {options.map((o) => (
        <option key={o.value || "all"} value={o.value}>{o.label}</option>
      ))}
    </select>
    <ChevronDownIcon />
  </div>
);

const ChevronDownIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
  >
    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.24 4.38a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
  </svg>
);
